/*
The pager under the list tabs, replacing @material-ui/core/TablePagination.

Prop names and handler signatures are MUI's on purpose - onChangePage(event, newPage) and
onChangeRowsPerPage(event), with `page` zero-based - so the six containers that own the
paging state did not have to change. event.target.value off a native <select> is the same
string MUI handed them, so their existing parseInt still applies.

MUI rendered its own icon buttons and a label reading "1-50 of 1234"; both are reproduced
here rather than dropped, because the count is the only place the tabs show how much data
is behind the filter.
*/
import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TablePagination = ({
  count, page, rowsPerPage, rowsPerPageOptions, onChangePage, onChangeRowsPerPage,
}) => {
  // Zero-based, and never negative when the list is empty.
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const from = count === 0 ? 0 : (page * rowsPerPage) + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  return (
    <div className="ps-pagination">
      <label className="ps-pagination__rows" htmlFor="ps-pagination-rows">
        <span>Rows per page:</span>
        <select
          id="ps-pagination-rows"
          value={rowsPerPage}
          onChange={onChangeRowsPerPage}
        >
          {rowsPerPageOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>

      <span className="ps-pagination__count">
        {`${from}-${to} of ${count}`}
      </span>

      <button
        type="button"
        className="ps-pagination__nav"
        aria-label="Previous page"
        disabled={page <= 0}
        onClick={event => onChangePage(event, page - 1)}
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        className="ps-pagination__nav"
        aria-label="Next page"
        disabled={page >= lastPage}
        onClick={event => onChangePage(event, page + 1)}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

TablePagination.propTypes = {
  count: PropTypes.number.isRequired,
  // Zero-based, as MUI had it.
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  // (event, newPage) => void
  onChangePage: PropTypes.func.isRequired,
  // (event) => void, read as event.target.value
  onChangeRowsPerPage: PropTypes.func.isRequired,
};

TablePagination.defaultProps = {
  rowsPerPageOptions: [50, 100, 200, 500, 1000],
};

export default TablePagination;
